import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	computedFrom,
	customElement,
	inlineView,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import React from 'react';

import { MemberSearchResult } from '~/api/member-search-api';
import { AppState } from '~/app-store';
import { BaseDialog } from '~/components/dialog/base-dialog';
import { EntitySearch } from '~/components/entity-search/entity-search';
import { EntitySearchActions } from '~/components/entity-search/entity-search-actions';
import { EntitySearchType } from '~/components/entity-search/entity-type';
import { NavigationState } from '~/components/navigation/navigation-state';
import { NavigationSearchHistory } from '~/components/navigation/navigation-types';
import { filterStatefulQueryParameters } from '~/components/navigation/navigation-utils';
import { ReactComponent } from '~/components/react-component/react-component';
import { Slideout } from '~/components/slideout/slideout';
import { CommonURLParam } from '~/constants';
import { ATOInterfaces } from '~/forms/ato-interfaces/ato-interfaces';
import { MemberDetails } from '~/forms/member-and-client-details/member-and-client-details';
import { PersonalDetails } from '~/forms/personal-details/personal-details';
import { EntityType } from '~/models/entities/entity-type';
import { Logger } from '~/utils/logger';
import { RouteState } from '~/utils/route-state';
import { Notification } from '~/components/notification/notification';

@autoinject
@customElement('entity-search-react')
@inlineView('<template ref="refContainer"></template>')
export class EntitySearchReact extends ReactComponent {
	/**
	 * These bindables should not technically be bound in templates,
	 * we leverage `@bindable` to trigger `propertyChanged` (rendering)
	 * as `@observable` doesn't do that.
	 */
	@bindable
	private searchHistory?: NavigationSearchHistory;

	@bindable
	private urlParams: { [name: string]: string } = {};

	@bindable
	private refreshMemberEntity?: boolean;

	private onRouterNavigationCompleteHandle: Subscription;
	private onSlideoutPostSaveHandle: Subscription;
	private onDialogCloseHandle: Subscription;
	private static navigationState: NavigationState;
	private static i18n: I18N;

	constructor(
		private readonly store: Store<StateHistory<AppState>>,
		private readonly eventAggregator: EventAggregator,
		private readonly router: Router,
		public i18n: I18N,
		private readonly navState: NavigationState,
		protected element: Element,
	) {
		super('entity-search-react', element);
		EntitySearchReact.navigationState = navState;
		EntitySearchReact.i18n = i18n;
		this.updateURLParams();
	}

	protected bind(bindingContext, overrideContext): void {
		super.bind(bindingContext, overrideContext);
		this.store.state.subscribe((state: StateHistory<AppState>) => {
			this.searchHistory = state.present.navigation.searchHistory;
		});
		this.onRouterNavigationCompleteHandle = this.eventAggregator.subscribe(
			'router:navigation:complete',
			this.onRouterNavigationComplete,
		);
		this.onSlideoutPostSaveHandle = this.eventAggregator.subscribe(
			Slideout.Events.PostSave,
			this.onMemberClientUpdate,
		);
		this.onDialogCloseHandle = this.eventAggregator.subscribe(
			BaseDialog.Events.CloseBaseDialog,
			this.onMemberClientUpdate,
		);
	}

	protected unbind(): void {
		this.onRouterNavigationCompleteHandle.dispose();
		this.onSlideoutPostSaveHandle.dispose();
		this.onDialogCloseHandle.dispose();
	}

	private updateURLParams = (): void => {
		const urlParams = {};
		const params = new URLSearchParams(window.location.hash.split('?')[1]);
		params.forEach((value, key) => {
			urlParams[key] = value;
		});
		this.urlParams = urlParams;
	};

	@computedFrom('router.currentInstruction.queryParams["nav-id"]')
	get navId(): string {
		return this.router.currentInstruction.queryParams[
			CommonURLParam.NAV_ID
		];
	}

	@computedFrom('router.currentInstruction.queryParams["nav-view"]')
	get navView(): string {
		return this.router.currentInstruction.queryParams[
			CommonURLParam.NAV_VIEW
		];
	}

	@computedFrom(
		'router.currentInstruction.queryParams["nav-view"],router.currentInstruction.queryParams["nav-id"]',
	)
	get searchAllDivision(): boolean {
		return EntitySearchReact.navigationState.getSearchAllDivisionStatus(
			this.navId,
			this.navView,
		);
	}

	private readonly onRouterNavigationComplete = (): void => {
		this.updateURLParams();
	};

	/**
	 * After updating a member or client record, we want to refresh the member
	 * entity search so any possible changes to the member entity are pushed through
	 */
	private readonly onMemberClientUpdate = (data: any): void => {
		if (
			data.source === PersonalDetails.FORM_NAME ||
			data.source === MemberDetails.FORM_NAME ||
			data.source === ATOInterfaces.FORM_NAME
		) {
			if (
				!this.urlParams[CommonURLParam.FUND] ||
				!this.urlParams[CommonURLParam.MEMBER]
			) {
				Logger.log(
					'Member not selected. Not refreshing member entity search.',
				);
				return;
			}
			this.refreshMemberEntity = true;
		}
	};

	private readonly setUpdatedSearchHistory = (
		updateClientModel: MemberSearchResult,
	): void => {
		this.store.dispatch(
			EntitySearchActions.AddEntityToHistory,
			updateClientModel,
		);
	};

	private readonly onSelectEntity = (
		focusedEntity: EntitySearchType | undefined,
	): void => {
		if (!focusedEntity) {
			// NOTE: Jake: 2019-03-15 - WEBUI-1672
			// Only update route parameters if focusedEntity was changed otherwise
			// customisation editor can end up in an infinite redirect loop.
			// Remove entity releated params
			const params = EntitySearchReact.createURLParamsFromEntity(null);
			Logger.log('Clearing entity...');
			RouteState.navigateToSameURLWithNewRouteParameters(
				this.router,
				params,
			);
		} else {
			const params = EntitySearchReact.createURLParamsFromEntity(
				focusedEntity,
				this.navId,
			);
			if (Object.keys(params).length > 0) {
				this.store.dispatch(
					EntitySearchActions.AddEntityToHistory,
					focusedEntity,
				);
				RouteState.navigateToSameURLWithNewRouteParameters(
					this.router,
					params,
				);
			}
		}
	};

	/**
	 * Create URL params for entity
	 */
	private static createURLParamsFromEntity(
		entity: EntitySearchType | null | undefined,
		navId?: string,
	): { [props: string]: string | undefined } {
		const params: { [props: string]: string | undefined } = {
			// NOTE: Jake: 2019-10-18 - WEBUI-2529
			// Not sure why "category" needs to be null but leaving it for now
			// just in-case.
			category: null,
		};

		// NOTE: Jake: 2019-03-29 - WEBUI-1991
		// Clear form parameters. This is to avoid cases occuring where "employer" and "member"
		// could be set at the same time.
		// ie. If you change from having selected an employer to a member.
		// Member+Employer reset
		params[CommonURLParam.FUND] = null;
		// Member-only reset
		params[CommonURLParam.CLIENT] = null;
		params[CommonURLParam.MEMBER] = null;
		// Employer-only reset
		params[CommonURLParam.EMPLOYER] = null;
		// Investment Manager-only reset
		params[CommonURLParam.INVESTMENT_MANAGER] = null;
		// Adviser-only reset
		params[CommonURLParam.ADVISER] = null;

		if (!entity) {
			// NOTE: Jake: 2019-03-30 - WEBUI-1991
			// If "null" is passed into this method, we want to specifically "null" out
			// all params related to the selected entity.
			return params;
		}

		const urlParams = new URLSearchParams(
			window.location.hash.split('?')[1],
		);

		// NOTE: Jake: 2019-03-15 - WEBUI-1672
		// We don't want "nav-id" to be falsey (ie. cleared from the URL) or else the router can be caught
		// in an infinite loop after forcing the clearing of the value.
		// NOTE(Albert): 2020-07-30 - WEBUI-3507
		// Only fall back to setting nav-id to entityType name if nav-id is not already set.
		// If nav-id is set and entityType is not `EMPTY`, always setting nav-id to entityType
		// causes entity search and navigation menu items to disappear when entity is selected
		params[CommonURLParam.NAV_ID] = navId ? navId : entity.entityType;
		// NOTE(Jae): 2020-03-04 - WEBUI-3169
		// Add nav-view parameter to current params so that filterStatefulQueryParameters() works
		const navigationViewUrlParam = urlParams.get(CommonURLParam.NAV_VIEW);
		params[CommonURLParam.NAV_VIEW] = navigationViewUrlParam;

		if (entity.entityType === EntityType.MEMBER) {
			if (
				this.navigationState.getSearchAllDivisionStatus(
					navId,
					navigationViewUrlParam,
				)
			) {
				// In the search history, there may have existing entities missing FRc_division info
				if (
					entity.values.FRc_Division === undefined ||
					entity.values.FRc_Division.length !== 1
				) {
					Notification.warning(
						this.i18n.tr(
							'warnings.missing-division-in-search-history',
						),
					);
					return params;
				}
				const viewName =
					this.navigationState.getNavigationDivisionViewEntity(
						navId,
						navigationViewUrlParam,
						entity.values.FRc_Division,
					);
				// missing sister views of certain division
				if (viewName === undefined) {
					const division = entity.values.FRc_Division;
					Notification.error(
						this.i18n.tr('errors.division-view-missing--exact', {
							division,
						}) +
							' ' +
							this.i18n.tr('errors.cannot-select-this-member'),
					);
					return params;
				}
				params[CommonURLParam.NAV_VIEW] = viewName;
				params[CommonURLParam.DIVISION] = entity.values.FRc_Division;
			} else {
				// In the search history, there may have existing entities from other division
				if (
					urlParams.get(CommonURLParam.DIVISION) !==
					entity.values.FRc_Division
				) {
					Notification.warning(
						this.i18n.tr('warnings.not-enable-search-all-division'),
					);
					return params;
				}
			}
		}

		switch (entity.entityType) {
			case EntityType.MEMBER: {
				// NOTE: Jake: 2019-03-15 - WEBUI-1672
				// Falsey means that it will clear those values from the URL params.
				params[CommonURLParam.CLIENT] = entity.values.D2z_Client;
				params[CommonURLParam.FUND] = entity.values.MDz_Fund;
				params[CommonURLParam.MEMBER] = entity.values.MDz_Member;
				break;
			}

			case EntityType.EMPLOYER: {
				params[CommonURLParam.FUND] = entity.values.COz_Fund;
				params[CommonURLParam.EMPLOYER] = entity.values.COz_Payroll;
				break;
			}

			case EntityType.INVESTMENT_MANAGER: {
				params[CommonURLParam.INVESTMENT_MANAGER] =
					entity.values.IMz_Code;
				break;
			}

			case EntityType.ADVISER: {
				params[CommonURLParam.ADVISER] = entity.values.BDz_Name;
				break;
			}

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}

		return filterStatefulQueryParameters(params);
	}

	protected render() {
		let searchHistory: EntitySearchType[] = [];
		if (this.searchHistory) {
			const searchHistoryListForNav = this.searchHistory[this.navId];
			if (searchHistoryListForNav) {
				searchHistory = searchHistoryListForNav;
			}
		}
		const entity = this.navState.getEntityFromID(this.navId, this.navView);
		this.renderReactDOM(
			<EntitySearch
				urlParams={this.urlParams}
				entityType={entity ? entity.entityType : EntityType.EMPTY}
				entityDisplayName={entity ? entity.displayName : ''}
				quickItems={this.navState.getQuickLinks(
					this.navId,
					this.navView,
				)}
				searchAllDivision={this.searchAllDivision}
				onSelectEntity={this.onSelectEntity}
				searchHistoryList={searchHistory}
				refreshMemberEntity={this.refreshMemberEntity}
				setUpdatedSearchHistory={this.setUpdatedSearchHistory}
			/>,
		);
		// prevent member entity from refreshing after it has already been rendered
		this.refreshMemberEntity = false;
	}
}
